import { useEffect, useState } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { StateBlock } from '@/components/reports/StateBlock';
import { IpAllowlistSettings } from '@/components/settings/IpAllowlistSettings';
import { MfaPolicySettings } from '@/components/settings/MfaPolicySettings';
import { ProjectTypeSettings } from '@/components/settings/ProjectTypeSettings';
import { TenantDataSection } from '@/components/settings/TenantDataSection';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Stat, StatRow } from '@/components/ui/stat';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api, type MfaUnenrolled, type SeatUsage, type TenantSettings } from '@/lib/api';
import { useApi } from '@/lib/useApi';

/**
 * テナント設定。
 *
 * ここの既定単価が単価フォールバックの終端。0 のままだと収支レポートが
 * 全部 0 円になるので、他のどの設定より先に埋めてもらう必要がある。
 * 解決順序そのものは monthly_summary view にしかない。
 */
export function TenantSettingsPage({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useApi<{
    tenant: TenantSettings;
    seats: SeatUsage;
    mfaUnenrolled: MfaUnenrolled;
  }>('/api/tenant/settings');

  return (
    <>
      <PageHeader title="テナント設定" />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        {/* 保存後の再読み込みでフォームごと消えないよう、初回だけ読み込み表示にする */}
        <StateBlock loading={loading && data === null} error={error}>
          {data ? (
            <>
              <StatRow className="grid-cols-2">
                <Stat
                  label="Full Management (利用中 / 枠)"
                  value={`${data.seats.full.activeUsers} / ${data.seats.full.availableSeats}`}
                  hint={`残り ${data.seats.full.remainingSeats} 席`}
                  tone="brand"
                />
                <Stat
                  label="MCP Connect (利用中 / 枠)"
                  value={`${data.seats.mcp.activeUsers} / ${data.seats.mcp.availableSeats}`}
                  hint={`残り ${data.seats.mcp.remainingSeats} 席`}
                />
              </StatRow>
              <SettingsForm tenant={data.tenant} isAdmin={isAdmin} onSaved={reload} />
              <MfaPolicySettings
                policy={data.tenant.mfaPolicy}
                unenrolled={data.mfaUnenrolled}
                isAdmin={isAdmin}
                onSaved={reload}
              />
              {/* 種別は行ごとに保存するので、基本情報のフォームとは独立させる */}
              <ProjectTypeSettings isAdmin={isAdmin} />
              {/* 接続元の制限は管理者だけが触る。API も admin 限定なので、
                  member に出してもエラー表示になるだけ。 */}
              {isAdmin ? <IpAllowlistSettings isAdmin={isAdmin} /> : null}
              <TenantDataSection
                isAdmin={isAdmin}
                status={data.tenant.status}
                deletionScheduledAt={data.tenant.deletionScheduledAt}
              />
            </>
          ) : null}
        </StateBlock>
      </div>
    </>
  );
}

function SettingsForm({
  tenant,
  isAdmin,
  onSaved,
}: {
  tenant: TenantSettings;
  isAdmin: boolean;
  onSaved: () => void;
}) {
  const [name, setName] = useState(tenant.name);
  const [timezone, setTimezone] = useState(tenant.timezone);
  const [defaultHourlyCost, setDefaultHourlyCost] = useState(String(tenant.defaultHourlyCost));
  const [defaultHourlyRate, setDefaultHourlyRate] = useState(String(tenant.defaultHourlyRate));
  const [instruction, setInstruction] = useState(tenant.mcpCustomInstruction ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // 再読み込みで値が入れ替わったら、編集中でなければ追随させる
  useEffect(() => {
    setName(tenant.name);
    setTimezone(tenant.timezone);
    setDefaultHourlyCost(String(tenant.defaultHourlyCost));
    setDefaultHourlyRate(String(tenant.defaultHourlyRate));
    setInstruction(tenant.mcpCustomInstruction ?? '');
  }, [tenant]);

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      await api.patch('/api/tenant/settings', {
        name,
        timezone,
        defaultHourlyCost: Number(defaultHourlyCost),
        defaultHourlyRate: Number(defaultHourlyRate),
        mcpCustomInstruction: instruction,
      });
      setMessage('保存しました');
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const zeroRates = tenant.defaultHourlyCost === 0 && tenant.defaultHourlyRate === 0;

  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <section className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
        <h2 className="text-sm font-bold">基本情報</h2>

        <Field label="組織名">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isAdmin || busy}
          />
        </Field>

        <Field
          label="タイムゾーン"
          hint="MCP が「今日」を判定する基準です。UTC 固定にすると午前中の記録が前日に落ち、月次集計がずれます。"
        >
          <Input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Asia/Tokyo"
            className="font-mono"
            disabled={!isAdmin || busy}
          />
        </Field>

        <div className="flex flex-wrap gap-4">
          <Field label="既定の原価単価 (円/時)" className="w-44">
            <Input
              type="number"
              min={0}
              step={100}
              value={defaultHourlyCost}
              onChange={(e) => setDefaultHourlyCost(e.target.value)}
              className="font-mono"
              disabled={!isAdmin || busy}
            />
          </Field>
          <Field label="既定の売上単価 (円/時)" className="w-44">
            <Input
              type="number"
              min={0}
              step={100}
              value={defaultHourlyRate}
              onChange={(e) => setDefaultHourlyRate(e.target.value)}
              className="font-mono"
              disabled={!isAdmin || busy}
            />
          </Field>
        </div>

        <p className="text-xs leading-relaxed text-ink-subtle">
          単価は アサインの特別単価 → 個人の単価 → ここの既定値 の順に解決されます。
          {zeroRates ? (
            <span className="text-alert">
              {' '}
              いまは既定値が 0 円なので、収支レポートの金額がすべて 0 になります。
            </span>
          ) : null}
        </p>
      </section>

      <section className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
        <h2 className="text-sm font-bold">MCP の記録ルール (テナント全体)</h2>
        <p className="text-xs leading-relaxed text-ink-subtle">
          ここに書いた内容は、工数を記録する tool の説明と接続時の案内に連結されます。
          エンジニアに作業カテゴリを尋ねさせないための指示など、組織共通のルールを書いてください。
          プロジェクト個別のルールはプロジェクト画面で設定します。
        </p>
        <Textarea
          rows={8}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="例: 記録するときは対応した Issue 番号をメモに含めること"
          disabled={!isAdmin || busy}
        />
      </section>

      {isAdmin ? (
        <div className="flex items-center gap-4 lg:col-span-2">
          <Button variant="primary" onClick={save} disabled={busy}>
            保存
          </Button>
          {message ? <span className="text-xs text-ink-muted">{message}</span> : null}
        </div>
      ) : (
        <p className="text-xs text-ink-subtle lg:col-span-2">変更できるのは管理者だけです。</p>
      )}
    </div>
  );
}
